import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  CreditCard,
  User,
  ArrowRight,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  Copy,
  Building2,
  Key
} from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * AdminWithdrawals - Painel administrativo para gerenciar saques
 */
const AdminWithdrawals = () => {
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({});
  const [totalPaid, setTotalPaid] = useState(0);
  const [filter, setFilter] = useState('pending');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form states
  const [reviewForm, setReviewForm] = useState({
    status: '',
    rejection_reason: '',
    transaction_id: '',
    notes: ''
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadWithdrawals();
  }, [filter]);

  const loadWithdrawals = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API}/api/admin/withdrawals/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: filter !== 'all' ? filter : undefined, limit: 100 }
      });
      setWithdrawals(response.data.withdrawals || []);
      setStats(response.data.stats || {});
      setTotalPaid(response.data.total_paid || 0);
    } catch (error) {
      console.error('Erro ao carregar saques:', error);
      toast.error('Erro ao carregar saques');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (withdrawal) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/api/admin/withdrawals/${withdrawal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedWithdrawal(response.data);
      setReviewForm({
        status: '',
        rejection_reason: '',
        transaction_id: '',
        notes: ''
      });
      setShowModal(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes do saque');
    }
  };

  const handleReview = async (status) => {
    if (!selectedWithdrawal) return;
    
    if (status === 'rejected' && !reviewForm.rejection_reason) {
      toast.error('Informe o motivo da rejeição');
      return;
    }
    
    if (status === 'paid' && !reviewForm.transaction_id) {
      toast.error('Informe o ID da transação');
      return;
    }

    try {
      setActionLoading(true);
      const token = getToken();
      await axios.post(
        `${API}/api/admin/withdrawals/${selectedWithdrawal.withdrawal.id}/review`,
        { ...reviewForm, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Saque ${status === 'approved' ? 'aprovado' : status === 'rejected' ? 'rejeitado' : 'pago'} com sucesso!`);
      setShowModal(false);
      loadWithdrawals();
    } catch (error) {
      console.error('Erro ao processar saque:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar saque');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pendente' },
      approved: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: CheckCircle, label: 'Aprovado' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', icon: DollarSign, label: 'Pago' },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Rejeitado' },
      cancelled: { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: XCircle, label: 'Cancelado' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestão de Saques</h2>
          <p className="text-gray-400 text-sm">Aprovar e gerenciar pagamentos dos criadores</p>
        </div>
        <button
          onClick={loadWithdrawals}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-yellow-400 mb-2">
            <Clock className="w-5 h-5" />
            <span className="text-sm">Pendentes</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.pending || 0}</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-400 mb-2">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">Aprovados</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.approved || 0}</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-400 mb-2">
            <DollarSign className="w-5 h-5" />
            <span className="text-sm">Pagos</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.paid || 0}</p>
        </div>
        
        <div className="bg-gradient-to-br from-[#C79F5E]/20 to-[#8B6914]/10 border border-[#C79F5E]/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-[#C79F5E] mb-2">
            <CreditCard className="w-5 h-5" />
            <span className="text-sm">Total Pago</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(totalPaid)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: 'pending', label: 'Pendentes', count: stats.pending },
          { value: 'approved', label: 'Aprovados', count: stats.approved },
          { value: 'paid', label: 'Pagos', count: stats.paid },
          { value: 'rejected', label: 'Rejeitados', count: stats.rejected },
          { value: 'all', label: 'Todos' }
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === f.value
                ? 'bg-[#C79F5E] text-black'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            {f.label} {f.count !== undefined && `(${f.count})`}
          </button>
        ))}
      </div>

      {/* Withdrawals List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#C79F5E] animate-spin" />
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="text-center py-12 bg-white/5 rounded-xl">
          <DollarSign className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">Nenhum saque encontrado</p>
        </div>
      ) : (
        <div className="bg-white/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Criador</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Valor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">PIX</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {withdrawals.map((w) => (
                  <tr key={w.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C79F5E] to-[#8B6914] flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{w.creator_name}</p>
                          <p className="text-xs text-gray-400">{w.creator_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white font-bold">{formatCurrency(w.amount)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-gray-300 text-sm font-mono">{w.pix_key?.substring(0, 15)}...</p>
                      <p className="text-xs text-gray-500">{w.pix_key_type}</p>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(w.status)}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-gray-300 text-sm">{formatDate(w.requested_at)}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() => openDetails(w)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-white">Detalhes do Saque</h3>
                  <p className="text-sm text-gray-400">ID: {selectedWithdrawal.withdrawal.id}</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg text-gray-400"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Valor e Status */}
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-4">
                <div>
                  <p className="text-sm text-gray-400">Valor Solicitado</p>
                  <p className="text-3xl font-bold text-[#C79F5E]">
                    {formatCurrency(selectedWithdrawal.withdrawal.amount)}
                  </p>
                </div>
                {getStatusBadge(selectedWithdrawal.withdrawal.status)}
              </div>

              {/* Dados do Criador */}
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <User className="w-4 h-4 text-[#C79F5E]" />
                  Dados do Criador
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Nome</p>
                    <p className="text-white">{selectedWithdrawal.creator?.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Email</p>
                    <p className="text-white">{selectedWithdrawal.creator?.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Total de Ganhos</p>
                    <p className="text-green-400 font-semibold">
                      {formatCurrency(selectedWithdrawal.creator_total_earnings)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dados Bancários */}
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <Key className="w-4 h-4 text-[#C79F5E]" />
                  Chave PIX
                </h4>
                <div className="flex items-center justify-between bg-black/30 rounded-lg p-3">
                  <div>
                    <p className="text-xs text-gray-400 uppercase">{selectedWithdrawal.withdrawal.pix_key_type}</p>
                    <p className="text-white font-mono">{selectedWithdrawal.withdrawal.pix_key}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedWithdrawal.withdrawal.pix_key)}
                    className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                
                {selectedWithdrawal.withdrawal.bank_data && Object.keys(selectedWithdrawal.withdrawal.bank_data).length > 0 && (
                  <div className="mt-3">
                    <h5 className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Conta Bancária (Backup)
                    </h5>
                    <div className="bg-black/30 rounded-lg p-3 text-sm grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-gray-400">Banco:</span>
                        <span className="text-white ml-2">{selectedWithdrawal.withdrawal.bank_data.bank_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Agência:</span>
                        <span className="text-white ml-2">{selectedWithdrawal.withdrawal.bank_data.agency}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Conta:</span>
                        <span className="text-white ml-2">{selectedWithdrawal.withdrawal.bank_data.account_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Tipo:</span>
                        <span className="text-white ml-2">{selectedWithdrawal.withdrawal.bank_data.account_type}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                <h4 className="text-white font-semibold">Timeline</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Solicitado em:</span>
                    <span className="text-white">{formatDate(selectedWithdrawal.withdrawal.requested_at)}</span>
                  </div>
                  {selectedWithdrawal.withdrawal.reviewed_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Revisado em:</span>
                      <span className="text-white">{formatDate(selectedWithdrawal.withdrawal.reviewed_at)}</span>
                    </div>
                  )}
                  {selectedWithdrawal.withdrawal.paid_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pago em:</span>
                      <span className="text-green-400">{formatDate(selectedWithdrawal.withdrawal.paid_at)}</span>
                    </div>
                  )}
                  {selectedWithdrawal.withdrawal.transaction_id && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">ID Transação:</span>
                      <span className="text-white font-mono">{selectedWithdrawal.withdrawal.transaction_id}</span>
                    </div>
                  )}
                  {selectedWithdrawal.withdrawal.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <span className="text-red-400 text-xs">Motivo da rejeição:</span>
                      <p className="text-white">{selectedWithdrawal.withdrawal.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {selectedWithdrawal.withdrawal.status === 'pending' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Observações (opcional)</label>
                    <textarea
                      value={reviewForm.notes}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white resize-none"
                      rows={2}
                      placeholder="Adicionar observações..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Motivo da Rejeição (se rejeitar)</label>
                    <input
                      type="text"
                      value={reviewForm.rejection_reason}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, rejection_reason: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                      placeholder="Ex: Dados bancários incorretos"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleReview('rejected')}
                      disabled={actionLoading}
                      className="flex-1 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold rounded-xl transition-all disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Rejeitar'}
                    </button>
                    <button
                      onClick={() => handleReview('approved')}
                      disabled={actionLoading}
                      className="flex-1 py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-semibold rounded-xl transition-all disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Aprovar'}
                    </button>
                  </div>
                </div>
              )}

              {selectedWithdrawal.withdrawal.status === 'approved' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">ID da Transação Bancária *</label>
                    <input
                      type="text"
                      value={reviewForm.transaction_id}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, transaction_id: e.target.value }))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
                      placeholder="Ex: E12345678901234567890123456789012"
                    />
                  </div>
                  
                  <button
                    onClick={() => handleReview('paid')}
                    disabled={actionLoading || !reviewForm.transaction_id}
                    className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                      <>
                        <DollarSign className="w-5 h-5 inline mr-2" />
                        Marcar como Pago
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawals;
